import React from 'react'

import HeaderMeta from 'components/HeaderMeta'

import Content from 'components/base/Content'
import Link from 'components/base/Link'
import Title from 'components/base/Title'
import Paragraph, { Theme as ParagraphTheme } from 'components/base/Paragraph'
import SectionRule from 'components/base/SectionRule/styles'

import PageHero, { Theme as PageHeroTheme } from 'components/blocks/PageHero'
import ProductListItem from 'components/blocks/ProductListItem'
import CtaQuote from 'components/blocks/CtaQuote'
import { Theme as InsuranceTypeIconTheme } from 'components/base/Icon/InsuranceTypeIcon'

const Products: React.FC = (): React.ReactElement => (
  <div>
    <HeaderMeta
      title='Insurance Products | Insurance Lounge'
      description='Insurance Lounge offers great plans in all major insurance categories. We provide personalized home, auto, life, health, & more insurance recommendations.'
      ogImg='/images/og-images/Products.webp'
    />
    <PageHero theme={PageHeroTheme.productlist}>
      Our Insurance Products
    </PageHero>
    <Content margined={true} center={true} short={true}>
      <Title level={2}>Looking for insurance?</Title>
      <Paragraph theme={ParagraphTheme.introLarge}>
        Insurance Lounge offers great plans in all major insurance categories. We will make personalized recommendations in home, auto, life, health, and more. View our insurance products below to get more details.
      </Paragraph>
      <ProductListItem
        title='Auto Insurance'
        icon={InsuranceTypeIconTheme.auto}
        link='/products/auto-insurance'
      >
        <Paragraph>
          If you’re new to <Link to='/products/auto-insurance'>auto insurance</Link>, want to switch to a different company, or are looking for a lower rate, you’ve come to the right place. Car insurance is essential not only because the law requires it, but also because you need it to protect yourself, your passengers, and your assets.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Homeowners Insurance'
        icon={InsuranceTypeIconTheme.home}
        link='/products/homeowners-insurance'
      >
        <Paragraph>
          Your home is one of your largest investments, which is why it’s necessary to protect it inside and out with <Link to='/products/homeowners-insurance'>homeowners insurance</Link>. At Insurance Lounge, we will compare rates across numerous providers to find the best rate and coverage for your home in Beaverton, Grants Pass, or Medford.
        </Paragraph>
      </ProductListItem>
    </Content>
    <CtaQuote
      trackingInfo={{
        button_position: 'middle',
        page_category: 'products_page',
      }}
    />
    <Content margined={true} center={true} short={true}>
      <ProductListItem
        title='Life Insurance'
        icon={InsuranceTypeIconTheme.life}
        link='/products/life-insurance'
      >
        <Paragraph>
          It may not always seem like a priority, but purchasing a <Link to='/products/life-insurance'>life insurance</Link> policy is one of the most responsible choices you can make for yourself and your dependents. By purchasing a policy, you’re investing in your family’s future. Life insurance is a tool that can protect your spouse, children, or other family members from the financial losses that can result if something happens to you.
        </Paragraph>
        <Paragraph>
          If you’re considering life insurance for yourself, you’re making a smart financial move. To help you make the right life insurance selection, we will work with you to consider your situation and needs. Are you looking for lifetime coverage or just a specific period? How much coverage will you need to protect your family when you’re gone?
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Health Insurance'
        icon={InsuranceTypeIconTheme.health}
        link='/products/health-insurance'
      >
        <Paragraph>
          Selecting the right health insurance plan can be complicated. PPO,
          HMO, HSA ⁠– there are multiple terms and situations to understand.
          At Insurance Lounge, we want to be your partner and help you
          through every step of the insurance vetting process for yourself or
          for your employers.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Medicare Insurance'
        icon={InsuranceTypeIconTheme.medicare}
        link='/products/medicare-insurance'
      >
        <Paragraph>
          <Link to='/products/medicare-insurance'>Medicare</Link> is a federal healthcare program for U.S. citizens and legal permanent residents of at least five straight years who are 65 or older, or any age with certain types and lengths of disabilities. Some parts of Medicare are included at no cost to all who meet these criteria. Others can require a premium payment, and additional coverage is provided by separate plans offered by private insurance companies.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Disability Insurance'
        icon={InsuranceTypeIconTheme.disability}
        link='/products/disability-insurance'
      >
        <Paragraph>
          When you encounter a disability that prevents you from working and making money, you still need income to cover expenses. With <Link to='/products/disability-insurance'>disability insurance</Link>, you can cover a portion of your income if you’re unable to work for an extended period. Because disabilities can arise suddenly, it’s wise to have this type of insurance even if you don’t need it immediately.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Business Insurance'
        icon={InsuranceTypeIconTheme.business}
        link='/products/business-insurance'
      >
        <Paragraph>
          We know and appreciate that no two businesses are alike. Each company has its own unique needs based on its industry, location, growth plan, and other considerations. As a business owner, it’s important to speak with an Insurance Concierge to discuss all your options regarding <Link to='/products/business-insurance'>commercial insurance</Link> and the future protection of your business.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <ProductListItem
        title='Investments & Retirement'
        icon={InsuranceTypeIconTheme.retirement}
        link='/products/investments-retirement'
      >
        <Paragraph>
          Managing your financial portfolio can be a full-time job; there is a lot you need to know to make informed <Link to='/products/investments-retirement'>investment decisions</Link>. That’s why many people decide to turn to a financial advisor for direction and advice on managing their funds.
        </Paragraph>
      </ProductListItem>
      <SectionRule />
      <Title level={2}>About Insurance Lounge</Title>
      <Paragraph>
        Insurance Lounge is a no-commission agency; our priority is finding the right coverage for your family’s needs. <Link to='/quote/new'>Get a quote</Link> today in the way most convenient for you, whether in-person, online, or by calling our team at (800) 211-8660.
      </Paragraph>
    </Content>
  </div>
)

export default Products
